import type { WebStatus } from "@shared/services/pim/types";

/**
 * Display-ready status used by the products table. Extends the PIM `WebStatus`
 * union with Salesforce-approval-derived values that surface before a SKU has
 * been marked visible.
 */
export type ResolvedStatus = WebStatus | "Pending" | "Rejected";

/**
 * Resolves a display-ready webStatus from a variant's webStatus (PIM) and
 * status (Salesforce `Approval_status__c`) fields.
 *
 * - `webStatus` present → pass through.
 * - `status === "SKU marked as visible"` → "Visible for Sale".
 * - `status === "Rejected"` → "Rejected".
 * - `status === "SKU Created"` or no status at all → "Not Visible".
 *   "SKU Created" is the approved-but-not-yet-published SF state; combined
 *   with plain installation variants (no PIM/SF metadata), both render with
 *   the neutral Not Visible badge rather than the in-flight Pending badge.
 * - Any other SF status (Draft / Submitted for Approval / Copywrite-*) →
 *   "Pending". These rows are dropped by `filterOutHiddenSalesforceStatuses`
 *   before they reach the table, so Pending is effectively reserved for code
 *   paths that bypass that filter. "Rejected" is NOT dropped by that filter —
 *   it surfaces in the table as a red badge.
 *
 * Lives in its own module so route-level and presentation-level utilities can
 * share it without pulling in unrelated table helpers (test-id generators,
 * image helpers, pricing utils).
 */
export const resolveWebStatus = (webStatus: WebStatus | undefined, status: string | undefined): ResolvedStatus => {
  if (webStatus) {
    return webStatus;
  }
  if (status === "SKU marked as visible") return "Visible for Sale";
  if (status === "Rejected") return "Rejected";
  if (!status || status === "SKU Created") return "Not Visible";
  return "Pending";
};
